import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Product, SiteConfig, HeroItem, Order, OrderStatus, LandingContent } from '../types';
import { DB } from '../services/db';
import { useLanguage, type LangCode } from '../services/languageContext';
import { normalizeVideoUrl, isEmbeddable, isVideoUrl } from '../services/videoUtils';
import { BlurImage } from './ui/BlurImage';
import { Lightbox } from './ui/Lightbox';
import { MarkdownEditor } from './ui/MarkdownEditor';
import { 
  Plus, Trash2, Save, LayoutDashboard, Package, 
  Settings, LogOut, ArrowLeft, DollarSign, 
  Eye, CheckCircle2, AlertTriangle, 
  Lock, Key, Loader2, Image as ImageIcon, FileText, 
  Tag, Percent, PlusCircle, X, Download, Link as LinkIcon, Edit3, MonitorPlay, Cpu,
  Video, EyeOff, GripVertical, TrendingUp, Calendar, Filter, RefreshCw, Search, Play, Sparkles, ShieldAlert, Globe, Clock, Move, Mail
} from 'lucide-react';
import { supabase } from '../services/supabaseClient';

interface AdminProps {
  products: Product[];
  siteConfig: SiteConfig;
  onAddProduct: (product: Product) => void;
  onUpdateProduct: (product: Product) => void;
  onDeleteProduct: (id: string) => void;
  onConfigUpdate: () => void;
  onExit: () => void;
}

type AdminView = 'finance' | 'inventory' | 'cms';

// --- SHARED COMPONENTS ---
const InputGroup = ({ label, icon: Icon, children }: any) => (
  <div className="mb-4">
    <label className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mb-2 flex items-center gap-2">
      {Icon && <Icon size={12} />} {label}
    </label>
    {children}
  </div>
);

// ⚡️ SMART THUMBNAIL (Fixed Black Screen with autoPlay)
const AdminThumbnail: React.FC<{ src: string, alt?: string, className?: string }> = ({ src, alt, className }) => {
    const cleanUrl = normalizeVideoUrl(src);
    const isVideo = isVideoUrl(cleanUrl);
    const isDriveEmbed = isVideo ? isEmbeddable(cleanUrl) : false;

    if (!src) return <div className={`bg-[#151515] ${className}`} />;

    if (isVideo) {
        if (isDriveEmbed) {
            return (
                <div className={`relative bg-black overflow-hidden ${className}`}>
                    <iframe 
                        src={cleanUrl} 
                        className="w-full h-full object-cover pointer-events-none scale-150" 
                        title="thumb"
                        loading="lazy"
                    />
                    <div className="absolute inset-0 bg-transparent z-10" />
                </div>
            );
        } else {
            return (
                <video 
                    src={cleanUrl} 
                    className={`object-cover bg-black opacity-80 ${className}`} 
                    muted 
                    playsInline
                    preload="metadata"
                    loop
                    autoPlay={true}
                />
            );
        }
    }

    return <BlurImage src={src} alt={alt || ''} className={className} />;
};

export const Admin: React.FC<AdminProps> = ({ products, siteConfig, onAddProduct, onUpdateProduct, onDeleteProduct, onConfigUpdate, onExit }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [currentView, setCurrentView] = useState<AdminView>('finance');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const { lang: adminLang, setLang: setAdminLang, t } = useLanguage();
  
  const [previewMedia, setPreviewMedia] = useState<{url: string, type: 'video' | 'image'} | null>(null);
  
  const allCategories = useMemo(() => {
      const derived = Array.from(new Set(products.flatMap(p => p.categories).filter(Boolean)));
      const defaults = ['workflow', 'lipsync', 'dance', 'photo', 'nsfw'];
      return Array.from(new Set([...defaults, ...derived]));
  }, [products]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
      setAuthLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    const result = await DB.login(emailInput, passwordInput);
    if (result.success) {
      setIsAuthenticated(true);
    } else {
      setLoginError(result.error || t('admin_err_invalid'));
      setPasswordInput('');
    }
    setLoginLoading(false);
  };

  const handleLogout = async () => {
    await DB.logout();
    setIsAuthenticated(false);
    onExit();
  };

  const handleEditProduct = async (product: Product) => {
    const fullProduct = await DB.getProductAdmin(product.id);
    setSelectedProduct(fullProduct || product);
  };

  const handleDelete = async (id: string) => {
    if(window.confirm("CONFIRM DELETION: This action is irreversible.")) {
      const success = await DB.deleteProduct(id);
      if (success) onDeleteProduct(id);
      else alert("Database Error");
    }
  };

  // Handle Global Category Deletion
  const handleDeleteCategory = async (cat: string) => {
      if (['workflow', 'lipsync', 'dance', 'photo', 'nsfw'].includes(cat)) {
          alert("Cannot delete core system categories.");
          return;
      }
      
      const confirmMsg = `WARNING: This will remove the category "${cat}" from ALL products in the database.\n\nAre you sure?`;
      if (window.confirm(confirmMsg)) {
          const success = await DB.removeCategoryFromAllProducts(cat);
          if (success) {
              onConfigUpdate(); 
          } else {
              alert("Failed to purge category.");
          }
      }
  };

  const switchAdminLang = (lang: LangCode) => {
      setAdminLang(lang);
      setAdminLang(lang);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-white" size={32} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-sm bg-[#0A0A0A] border border-white/10 rounded-2xl p-8 shadow-2xl relative overflow-hidden">
           <div className="absolute top-0 left-0 w-full h-[2px] bg-white opacity-20" />
           <div className="text-center mb-8">
              <h1 className="text-2xl font-display font-bold text-white tracking-widest">{t('admin_auth_title')}</h1>
              <p className="text-xs text-gray-500 mt-2 font-mono">{t('admin_auth_sub')}</p>
           </div>

           <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative group">
                <Mail className="absolute left-3 top-3 text-gray-500 group-focus-within:text-white transition-colors" size={16} />
                <input 
                  type="email" 
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="admin@ofmhub.net"
                  className="w-full bg-[#121212] border border-white/10 rounded-lg p-3 pl-10 text-white focus:border-white/30 outline-none text-xs font-mono tracking-widest placeholder:text-gray-700 transition-all"
                  autoFocus
                  autoComplete="email"
                />
              </div>
              <div className="relative group">
                <Key className="absolute left-3 top-3 text-gray-500 group-focus-within:text-white transition-colors" size={16} />
                <input 
                  type="password" 
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-[#121212] border border-white/10 rounded-lg p-3 pl-10 text-white focus:border-white/30 outline-none text-xs font-mono tracking-widest placeholder:text-gray-700 transition-all"
                  autoComplete="current-password"
                />
              </div>
              {loginError && (
                <div className="flex items-center justify-center gap-2 text-red-500 text-[10px] uppercase font-bold tracking-wider">
                  <AlertTriangle size={12} /> {loginError}
                </div>
              )}
              <button 
                type="submit" 
                disabled={loginLoading}
                className="w-full bg-white text-black font-bold py-3 rounded-lg text-xs uppercase tracking-widest hover:bg-gray-200 transition-colors shadow-[0_0_20px_rgba(255,255,255,0.1)] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loginLoading ? <Loader2 className="animate-spin" size={14} /> : null}
                {t('admin_btn_auth')}
              </button>
           </form>
           <div className="mt-8 text-center">
              <p className="text-[9px] text-gray-700 uppercase tracking-widest">Supabase Auth • Encrypted</p>
           </div>
        </div>
      </div>
    );
  }

  return (
    <div key={adminLang} className="min-h-[100dvh] min-h-screen bg-[#030304] text-white flex flex-col lg:flex-row font-sans">
      
      {/* Sidebar: horizontal tab bar on phone, rail on desktop */}
      <aside className="w-full lg:w-64 lg:shrink-0 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#050505] flex flex-col z-30">
        <div className="h-12 sm:h-14 lg:h-16 flex items-center justify-between lg:justify-start lg:px-6 border-b border-white/10 px-3 gap-2">
           <div className="flex items-center gap-2 min-w-0">
             <img src="https://lh3.googleusercontent.com/rd-d/ALs6j_Gkvl9xgMaWh2LGR8Y5xiS0au5rMfY2o8sHQyx8MQgJ4I2U-XDXbgE65799Es575wm7EooTgLBUF_YDeZ2RO7yU9TIh8siQEOz479vrKjVwqS7ObAACHPCWfRGWXaSgGIiE4vv7UY5xQI4puchcfV0EKFkpQBRH1s5cq2g9_9DXh8BQc9zM8rdoJs73vmst-Vi2H7oFBZUMX5XNj52S2nRF0vrpT2Iz3TQUSBDs2UR7zhzPmOd6kgC1e5S7OEGqRyVGsBU8YKXxHomMmBFdSCibCVUMn1oQpM2ukr7VNKOwpWE43k2wiHB63MN_ObIchrByidbAThmpMyJbNYZp7cFcppp9jUDcIGotoyUOmk3NHSuqC-i2Mllp1liB073ZJv_-lIZMVdZX6DKMYMXsSwgegtl-_8a5hXgFB3IZfwjIRTd7jGdSB_ZDIOIOCeBwMaxMkR5z593w2tHiticQOfU1UYfu9ceg-kWwy9yG71E_dZoH5krVDgIxJdibBVCTELpCiH3SIPriVmv9_E650s0d865ZTDlqT63QUsjfS-JrC-Q45VpjcmSThqHQm3jgI8fAxCqOg0oifTq7e4izchwwNxOZThDbyrBhMqiVPpsqza4hUdN2gfvDstpDN8V0yN0dDM4Jagaz3mwfIae6miUduFdSFUE80Los64dOs1rTKbBR0qdSUm3k4Z5XDXt_Y66Skcc_3wyBtMvoVJWcBeODjAFmzOf5bGRKe_h6rhbSkUk60Q3D6y4j6fsfnJeRhz4VcEk6tDpsQiNYwaBpYO84HPnxuew5ZYDklONwg7xdvJM4UDzo7jc1RsQ3oTj-Wk2HyRl4P4Ebi3qsr562khKvlg8iKq-jFvzhOG9eDPSkUcU0IFPeikq1Hy9oQ-OKTHJp96zUmbc5ju2WGHmTw1eExPPyjaUSt5jzk3npgrH6BZlicy1PkTm1utHx41KoBllYiC3lRhw7JRHSFubpL0gP0W0ZTpmCZJsgs67iPASBq_LCbmVMu1adLHB00EztiWLtrkKnefWLmPkoTfyPK89Sf5YdIzZf9fU3E1mAH14QdfUG5OqcB4-zdps2FGuzzA_yi_BG_kX7qt_EmK6qquCwwBbvVt_iMDqtNAzcy9wflXsnvx3Mzzk416C4pQ1W4a4Ozdf8lU6ppJdQu2zRZyoCwA8cy7c1sJ9ACLAvTmyzR0ZK4_DKgk_0Qfudf_DyZqbMywAavk88rLLKVoGV_yirxlWkn-lEaHvbx5xXvQRP=w2880-h1640?auditContext=prefetch" alt="Admin" className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/10 border border-white/20 shrink-0" />
             <span className="font-bold tracking-wider text-xs sm:text-sm truncate hidden sm:inline lg:block">ADMIN v2</span>
           </div>
           <button type="button" onClick={handleLogout} className="lg:hidden p-2 rounded-lg text-red-500 hover:bg-red-500/10 shrink-0" aria-label={t('admin_dash_logout')}>
             <LogOut size={18} />
           </button>
        </div>
        <nav className="flex flex-row lg:flex-col flex-1 lg:flex-none p-2 lg:p-4 gap-1 lg:space-y-2 lg:space-y-0 min-h-0">
           <button type="button" aria-label={t('admin_dash_overview')} onClick={() => { setCurrentView('finance'); setSelectedProduct(null); }} className={`flex-1 lg:flex-none min-h-[48px] lg:min-h-0 flex flex-col lg:flex-row items-center justify-center lg:justify-start gap-1 lg:gap-3 px-2 lg:px-3 py-2.5 rounded-lg text-[10px] sm:text-xs font-bold uppercase tracking-wider transition-colors ${currentView === 'finance' ? 'bg-white text-black' : 'text-gray-500 hover:text-white hover:bg-white/5'}`}>
             <TrendingUp size={18} className="shrink-0" />
             <span className="truncate text-center max-w-[5.5rem] sm:max-w-[7rem] lg:max-w-none lg:text-left leading-tight">{t('admin_dash_overview')}</span>
           </button>
           <button type="button" aria-label={t('admin_dash_inventory')} onClick={() => { setCurrentView('inventory'); setSelectedProduct(null); }} className={`flex-1 lg:flex-none min-h-[48px] lg:min-h-0 flex flex-col lg:flex-row items-center justify-center lg:justify-start gap-1 lg:gap-3 px-2 lg:px-3 py-2.5 rounded-lg text-[10px] sm:text-xs font-bold uppercase tracking-wider transition-colors ${currentView === 'inventory' ? 'bg-white text-black' : 'text-gray-500 hover:text-white hover:bg-white/5'}`}>
             <Package size={18} className="shrink-0" />
             <span className="truncate text-center max-w-[5.5rem] sm:max-w-[7rem] lg:max-w-none lg:text-left leading-tight">{t('admin_dash_inventory')}</span>
           </button>
           <button type="button" aria-label={t('admin_dash_cms')} onClick={() => { setCurrentView('cms'); setSelectedProduct(null); }} className={`flex-1 lg:flex-none min-h-[48px] lg:min-h-0 flex flex-col lg:flex-row items-center justify-center lg:justify-start gap-1 lg:gap-3 px-2 lg:px-3 py-2.5 rounded-lg text-[10px] sm:text-xs font-bold uppercase tracking-wider transition-colors ${currentView === 'cms' ? 'bg-white text-black' : 'text-gray-500 hover:text-white hover:bg-white/5'}`}>
             <MonitorPlay size={18} className="shrink-0" />
             <span className="truncate text-center max-w-[5.5rem] sm:max-w-[7rem] lg:max-w-none lg:text-left leading-tight">{t('admin_dash_cms')}</span>
           </button>
        </nav>
        <div className="hidden lg:block p-4 border-t border-white/10">
          <button type="button" onClick={handleLogout} className="w-full flex items-center justify-center lg:justify-start gap-3 px-3 py-3 rounded-lg text-xs font-bold uppercase tracking-wider text-red-500 hover:bg-red-500/10 transition-colors">
            <LogOut size={18} /> <span>{t('admin_dash_logout')}</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col relative overflow-hidden bg-[#030304] min-w-0 min-h-0">
        <header className="min-h-[3.5rem] sm:h-16 border-b border-white/10 flex flex-wrap items-center justify-between gap-2 px-3 sm:px-4 md:px-6 py-2 sm:py-0 bg-[#050505]">
           <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
             {selectedProduct && <button type="button" onClick={() => setSelectedProduct(null)} className="p-2 hover:bg-white/10 rounded-full transition-colors shrink-0"><ArrowLeft size={18} /></button>}
             <h2 className="text-[11px] sm:text-sm font-bold uppercase tracking-widest text-gray-300 truncate">
               {selectedProduct ? t('admin_edit_artifact') : (
                 currentView === 'finance' ? t('admin_dash_overview') : 
                 currentView === 'cms' ? t('admin_dash_cms') : t('admin_dash_inventory')
               )}
             </h2>
           </div>
           
           <div className="flex items-center gap-2 sm:gap-3 shrink-0">
               <div className="flex items-center gap-0.5 sm:gap-1 border border-white/10 rounded-full px-1.5 sm:px-2 py-0.5 sm:py-1 bg-white/5">
                    <Globe size={10} className="text-gray-400 mr-0.5 sm:mr-1 shrink-0 hidden sm:inline" />
                    <button type="button"
                      onClick={() => switchAdminLang('en')} 
                      className={`text-[9px] font-bold px-1.5 sm:px-2 py-0.5 rounded ${adminLang === 'en' ? 'bg-white text-black' : 'text-gray-500 hover:text-white'}`}
                    >
                      EN
                    </button>
                    <button type="button"
                      onClick={() => switchAdminLang('ru')} 
                      className={`text-[9px] font-bold px-1.5 sm:px-2 py-0.5 rounded ${adminLang === 'ru' ? 'bg-white text-black' : 'text-gray-500 hover:text-white'}`}
                    >
                      RU
                    </button>
               </div>

               <button type="button" onClick={onExit} className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-white transition-colors border border-white/10 px-2 sm:px-3 py-1 rounded whitespace-nowrap">{t('admin_view_site')}</button>
           </div>
        </header>

        <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-5 md:p-8 overscroll-y-contain touch-pan-y">
           {currentView === 'finance' && !selectedProduct && <FinanceView products={products} />}
           
           {currentView === 'inventory' && !selectedProduct && (
             <InventoryView 
               products={products} 
               onEdit={handleEditProduct}
               onDelete={handleDelete}
               onCreate={() => setSelectedProduct({
                 id: '', title: '', description: '', price: 0, originalPrice: 0, isOnSale: false, isNew: true, categories: ['workflow'],
                 coverImage: '', gallery: [], features: [], tags: [], is18Plus: false, digitalAssetUrl: '', digitalAssetInstructions: '', saleEndsAt: undefined
               } as Product)} 
             />
           )}

           {currentView === 'cms' && (
             <CMSView 
                products={products} 
                siteConfig={siteConfig} 
                onUpdate={onConfigUpdate}
                onPreview={(url, type) => setPreviewMedia({url, type})}
             />
           )}

           {selectedProduct && (
             <ProductDetailView 
               product={selectedProduct}
               availableCategories={allCategories} 
               onDeleteCategory={handleDeleteCategory}
               onSave={async (p) => {
                 const isNew = !p.id || p.id === '';
                 const cleanProduct = {
                    ...p,
                    demoVideoUrl: normalizeVideoUrl(p.demoVideoUrl),
                    gallery: p.gallery.map(normalizeVideoUrl)
                 };

                 let success = false;
                 if (isNew) {
                    const savedProduct = await DB.saveProduct(cleanProduct);
                    if (savedProduct) {
                        onAddProduct(savedProduct);
                        success = true;
                    }
                 } else {
                    success = await DB.updateProduct(cleanProduct);
                    if (success) {
                        onUpdateProduct(cleanProduct);
                    }
                 }
                 if (success) setSelectedProduct(null);
               }}
               onCancel={() => setSelectedProduct(null)}
             />
           )}
        </div>
      </main>

      {/* Lightbox for Admin Preview */}
      {previewMedia && (
        <Lightbox 
          src={previewMedia.url} 
          type={previewMedia.type} 
          onClose={() => setPreviewMedia(null)} 
        />
      )}
    </div>
  );
};

// ... FinanceView and InventoryView remain same as previous (omitted for brevity, assume they exist)
// Just placeholder them if you copy-paste, otherwise keep previous content.
// RE-INSERTING FinanceView and InventoryView for completeness in the file update.

const getStatusLabel = (status: OrderStatus, t: (k: string) => string): string => {
  const key = status === 'admin_created' ? 'admin_fin_filter_admin' :
    status === 'paid' ? 'admin_fin_filter_cryptobot' :
    status === 'pending' ? 'admin_fin_filter_pending' : 'admin_fin_filter_expired';
  return t(key);
};

const OrderModal: React.FC<{
  order: Order | null;
  products: Product[];
  onSave: (order: Order) => Promise<void>;
  onClose: () => void;
  isNew: boolean;
}> = ({ order, products, onSave, onClose, isNew }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState<Order>(order || {
    invoiceId: 0,
    status: 'admin_created',
    totalAmount: 0,
    currency: 'USD',
    items: [],
  });
  const [saving, setSaving] = useState(false);
  const [nextId, setNextId] = useState(0);

  useEffect(() => {
    if (isNew) {
      DB.getNextInvoiceId().then(id => setNextId(id));
    } else if (order) {
      setForm(order);
    }
  }, [order, isNew]);

  const addProduct = (p: Product) => {
    if (form.items.some(i => i.id === p.id)) return;
    setForm(prev => ({
      ...prev,
      items: [...prev.items, { id: p.id, title: p.title, price: p.price }],
      totalAmount: prev.totalAmount + p.price,
    }));
  };

  const removeItem = (id: string) => {
    const item = form.items.find(i => i.id === id);
    if (!item) return;
    setForm(prev => ({
      ...prev,
      items: prev.items.filter(i => i.id !== id),
      totalAmount: prev.totalAmount - item.price,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const payload: Order = {
      ...form,
      invoiceId: isNew ? nextId : form.invoiceId,
    };
    await onSave(payload);
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold uppercase tracking-widest mb-6">{isNew ? t('admin_fin_create_order') : t('admin_fin_edit_order')}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-[10px] text-gray-500 uppercase font-bold block mb-1">{t('admin_fin_invoice_id')}</label>
            <input
              type="number"
              value={isNew ? nextId : form.invoiceId}
              onChange={e => !isNew && setForm(prev => ({ ...prev, invoiceId: parseInt(e.target.value) || 0 }))}
              disabled={isNew}
              className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-white text-sm font-mono"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-500 uppercase font-bold block mb-1">{t('admin_fin_status')}</label>
            <select
              value={form.status}
              onChange={e => setForm(prev => ({ ...prev, status: e.target.value as OrderStatus }))}
              className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-white text-sm"
            >
              <option value="admin_created">{t('admin_fin_status_admin')}</option>
              <option value="paid">{t('admin_fin_status_cryptobot')}</option>
              <option value="pending">{t('admin_fin_status_pending')}</option>
              <option value="expired">{t('admin_fin_status_expired')}</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-gray-500 uppercase font-bold block mb-1">{t('admin_fin_add_product')}</label>
            <select
              onChange={e => {
                const id = e.target.value;
                if (id) {
                  const p = products.find(x => x.id === id);
                  if (p) addProduct(p);
                  e.target.value = '';
                }
              }}
              className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-white text-sm"
            >
              <option value="">{t('admin_fin_select_product')}</option>
              {products.filter(p => !form.items.some(i => i.id === p.id)).map(p => (
                <option key={p.id} value={p.id}>{p.title} — ${p.price}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-gray-500 uppercase font-bold block mb-1">{t('admin_fin_items')}</label>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {form.items.map(item => (
                <div key={item.id} className="flex justify-between items-center bg-[#121212] p-2 rounded">
                  <span className="text-sm truncate flex-1">{item.title}</span>
                  <span className="text-xs text-gray-500 mx-2">${item.price}</span>
                  <button type="button" onClick={() => removeItem(item.id)} className="text-red-500 hover:text-red-400"><X size={14} /></button>
                </div>
              ))}
              {form.items.length === 0 && <p className="text-xs text-gray-500">{t('admin_fin_no_items')}</p>}
            </div>
          </div>
          <div>
            <label className="text-[10px] text-gray-500 uppercase font-bold block mb-1">{t('admin_fin_total')}</label>
            <input
              type="number"
              step="0.01"
              value={form.totalAmount}
              onChange={e => setForm(prev => ({ ...prev, totalAmount: parseFloat(e.target.value) || 0 }))}
              className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-white text-sm font-mono"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg border border-white/10 text-gray-400">{t('admin_btn_cancel')}</button>
            <button type="submit" disabled={saving} className="flex-1 py-2 rounded-lg bg-white text-black font-bold disabled:opacity-50">
              {saving ? <Loader2 className="animate-spin inline" size={14} /> : t('admin_fin_save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FinanceView: React.FC<{ products: Product[] }> = ({ products }) => {
  const { t } = useLanguage();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | OrderStatus>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [creatingOrder, setCreatingOrder] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      const data = await DB.getAllOrders();
      setOrders(data);
      setIsLoading(false);
    };
    fetchOrders();
  }, [refreshTrigger]);

  const stats = useMemo(() => {
    const paidOrders = orders.filter(o => o.status === 'paid');
    const totalRevenue = paidOrders.reduce((acc, o) => acc + o.totalAmount, 0);
    const totalOrders = orders.length;
    const pendingOrders = orders.filter(o => o.status === 'pending' || o.status === 'admin_created').length;
    const avgOrderValue = paidOrders.length > 0 ? totalRevenue / paidOrders.length : 0;
    return { totalRevenue, totalOrders, pendingOrders, avgOrderValue };
  }, [orders]);

  const filteredOrders = useMemo(() => {
    return orders.filter(o => {
      const matchStatus = statusFilter === 'all' || o.status === statusFilter;
      const searchLower = searchTerm.toLowerCase();
      const matchSearch = 
        o.id?.toLowerCase().includes(searchLower) || 
        o.invoiceId.toString().includes(searchLower) ||
        o.items.some(i => i.title.toLowerCase().includes(searchLower));
      return matchStatus && matchSearch;
    });
  }, [orders, statusFilter, searchTerm]);

  const chartPoints = useMemo(() => {
    const days = 7;
    const points = [];
    const now = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(now.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      const dailyRevenue = orders
        .filter(o => o.status === 'paid' && o.createdAt?.startsWith(dateStr))
        .reduce((sum, o) => sum + o.totalAmount, 0);
      points.push({ date: dateStr, val: dailyRevenue });
    }
    return points;
  }, [orders]);

  const maxChartVal = Math.max(...chartPoints.map(p => p.val), 100);

  const handleSaveOrder = async (order: Order) => {
    if (editingOrder?.id) {
      const ok = await DB.updateOrder(editingOrder.id, order);
      if (ok) setRefreshTrigger(p => p + 1);
      else alert('Update failed');
      setEditingOrder(null);
    } else if (creatingOrder) {
      const ok = await DB.createOrder(order);
      if (ok) setRefreshTrigger(p => p + 1);
      else alert('Create failed');
      setCreatingOrder(false);
    }
  };

  const handleSetCryptoBot = async (invoiceId: number) => {
    const ok = await DB.updateOrderStatus(invoiceId, 'paid');
    if (ok) setRefreshTrigger(p => p + 1);
    else alert('Update failed');
  };

  const handleDeleteOrder = async (order: Order) => {
    if (!order.id) return;
    if (!confirm(`Delete order #${order.invoiceId}?`)) return;
    const ok = await DB.deleteOrder(order.id);
    if (ok) setRefreshTrigger(p => p + 1);
    else alert('Delete failed');
  };

  return (
    <div className="animate-fade-in space-y-8 pb-20">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-[#0A0A0A] p-6 rounded-2xl border border-white/10 relative overflow-hidden group">
           <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity"><DollarSign size={40}/></div>
           <h3 className="text-gray-500 text-[10px] uppercase font-bold tracking-widest mb-2">{t('admin_fin_revenue')}</h3>
           <p className="text-3xl font-mono font-bold text-white">${stats.totalRevenue.toFixed(2)}</p>
           <div className="mt-2 text-[10px] text-green-500 flex items-center gap-1"><TrendingUp size={10}/> {t('admin_fin_realtime')}</div>
        </div>
        <div className="bg-[#0A0A0A] p-6 rounded-2xl border border-white/10 relative overflow-hidden">
           <h3 className="text-gray-500 text-[10px] uppercase font-bold tracking-widest mb-2">{t('admin_fin_orders')}</h3>
           <p className="text-3xl font-mono font-bold text-white">{stats.totalOrders}</p>
        </div>
        <div className="bg-[#0A0A0A] p-6 rounded-2xl border border-white/10 relative overflow-hidden">
           <h3 className="text-gray-500 text-[10px] uppercase font-bold tracking-widest mb-2">{t('admin_fin_avg')}</h3>
           <p className="text-3xl font-mono font-bold text-white">${stats.avgOrderValue.toFixed(2)}</p>
        </div>
        <div className="bg-[#0A0A0A] p-6 rounded-2xl border border-white/10 relative overflow-hidden">
           <h3 className="text-gray-500 text-[10px] uppercase font-bold tracking-widest mb-2">{t('admin_fin_pending')}</h3>
           <p className={`text-3xl font-mono font-bold ${stats.pendingOrders > 0 ? 'text-yellow-500' : 'text-gray-500'}`}>{stats.pendingOrders}</p>
        </div>
      </div>
      <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6">
         <div className="flex justify-between items-center mb-6">
            <h3 className="text-sm font-bold uppercase tracking-widest text-white flex items-center gap-2"><Calendar size={14}/> {t('admin_fin_chart')}</h3>
         </div>
         <div className="h-48 w-full flex items-end gap-2 relative">
            {chartPoints.map((point, i) => {
               const heightPct = (point.val / maxChartVal) * 100;
               return (
                 <div key={i} className="flex-1 flex flex-col items-center gap-2 group relative">
                    <div className="absolute bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white text-black text-[10px] font-bold px-2 py-1 rounded">
                       ${point.val}
                    </div>
                    <div style={{ height: `${Math.max(heightPct, 2)}%` }} className={`w-full max-w-[40px] rounded-t-sm transition-all duration-500 ${point.val > 0 ? 'bg-indigo-500 hover:bg-indigo-400' : 'bg-white/5'}`} />
                    <span className="text-[9px] text-gray-600 uppercase font-mono">{point.date.slice(5)}</span>
                 </div>
               )
            })}
            <div className="absolute inset-0 pointer-events-none flex flex-col justify-between opacity-10">
               <div className="border-t border-white w-full h-0" />
               <div className="border-t border-white w-full h-0" />
               <div className="border-t border-white w-full h-0" />
            </div>
         </div>
      </div>
      <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl overflow-hidden">
         <div className="p-4 border-b border-white/10 flex flex-col md:flex-row gap-4 justify-between items-center">
            <div className="flex items-center gap-4 w-full md:w-auto flex-wrap">
               <button onClick={() => setCreatingOrder(true)} className="bg-white text-black hover:bg-gray-200 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider flex gap-2 items-center">
                  <Plus size={14} /> {t('admin_fin_create_order')}
               </button>
               <div className="relative group">
                  <Search className="absolute left-3 top-2.5 text-gray-600 group-focus-within:text-white" size={14} />
                  <input type="text" placeholder={t('admin_fin_search_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="bg-[#121212] border border-white/10 rounded-lg pl-9 pr-4 py-2 text-xs text-white focus:border-white/30 outline-none w-full max-w-[220px] sm:w-64 sm:max-w-none" />
               </div>
               <div className="flex bg-[#121212] rounded-lg p-1 border border-white/10 flex-wrap">
                  {(['all', 'admin_created', 'paid', 'pending', 'expired'] as const).map(s => (
                     <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-1.5 rounded text-[10px] font-bold uppercase transition-colors ${statusFilter === s ? 'bg-white text-black' : 'text-gray-500 hover:text-white'}`}>
                       {s === 'all' ? t('admin_fin_filter_all') : getStatusLabel(s as OrderStatus, t)}
                     </button>
                  ))}
               </div>
            </div>
            <button onClick={() => setRefreshTrigger(p => p + 1)} className="p-2 hover:bg-white/10 rounded-full transition-colors" title={t('admin_fin_refresh')}>
               <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
            </button>
         </div>
         <div className="overflow-x-auto overscroll-x-contain -mx-1 px-1 sm:mx-0 sm:px-0">
         <div className="min-w-[640px]">
         <div className="grid grid-cols-12 gap-2 sm:gap-4 bg-[#111] border-b border-white/5 p-3 sm:p-4 text-[9px] sm:text-[10px] uppercase font-bold text-gray-500 tracking-widest">
            <div className="col-span-2">{t('admin_fin_date_id')}</div>
            <div className="col-span-3">{t('admin_th_item')}</div>
            <div className="col-span-2">{t('admin_th_value')}</div>
            <div className="col-span-2">{t('admin_fin_status')}</div>
            <div className="col-span-3 text-right">{t('admin_th_controls')}</div>
         </div>
         <div className="divide-y divide-white/5 max-h-[min(55vh,500px)] sm:max-h-[500px] overflow-y-auto custom-scrollbar">
            {filteredOrders.length === 0 ? (
               <div className="p-8 text-center text-gray-600 text-xs uppercase tracking-widest">{t('admin_fin_no_transactions')}</div>
            ) : (
               filteredOrders.map(order => (
                  <div key={order.invoiceId} className="grid grid-cols-12 gap-2 sm:gap-4 p-3 sm:p-4 hover:bg-white/[0.02] transition-colors items-center group">
                     <div className="col-span-2">
                        <div className="text-xs font-mono text-gray-300">{new Date(order.createdAt || '').toLocaleDateString()}</div>
                        <div className="text-[9px] text-gray-600 font-mono">#{order.invoiceId}</div>
                     </div>
                     <div className="col-span-3">
                        <div className="flex flex-col gap-1">
                           {order.items.map((item, i) => (
                              <span key={i} className="text-[11px] text-white truncate block max-w-full">• {item.title}</span>
                           ))}
                        </div>
                     </div>
                     <div className="col-span-2 font-mono text-xs font-bold text-white">${order.totalAmount.toFixed(2)} <span className="text-gray-600 text-[9px]">{order.currency}</span></div>
                     <div className="col-span-2">
                        <span className={`px-2 py-1 rounded text-[9px] font-bold uppercase tracking-wider border ${
                          order.status === 'paid' ? 'bg-green-900/20 text-green-400 border-green-900/50' :
                          order.status === 'admin_created' ? 'bg-blue-900/20 text-blue-400 border-blue-900/50' :
                          order.status === 'pending' ? 'bg-yellow-900/20 text-yellow-400 border-yellow-900/50' :
                          'bg-red-900/20 text-red-400 border-red-900/50'
                        }`}>{getStatusLabel(order.status, t)}</span>
                     </div>
                     <div className="col-span-3 text-right flex justify-end gap-1">
                        <button onClick={() => setEditingOrder(order)} className="p-2 text-gray-500 hover:text-white transition-colors" title="Edit"><Edit3 size={14} /></button>
                        {order.status !== 'paid' && (
                          <button onClick={() => handleSetCryptoBot(order.invoiceId)} className="text-[9px] font-bold uppercase tracking-widest bg-green-500/20 hover:bg-green-500/30 text-green-400 px-3 py-1.5 rounded transition-all">{t('admin_fin_cryptobot_btn')}</button>
                        )}
                        {order.status === 'paid' && <CheckCircle2 size={16} className="text-green-500 inline-block opacity-50" />}
                        <button onClick={() => handleDeleteOrder(order)} className="p-2 text-gray-500 hover:text-red-500 transition-colors" title="Delete"><Trash2 size={14} /></button>
                     </div>
                  </div>
               ))
            )}
         </div>
         </div>
         </div>
      </div>

      {(editingOrder || creatingOrder) && (
        <OrderModal
          order={editingOrder}
          products={products}
          isNew={creatingOrder}
          onSave={handleSaveOrder}
          onClose={() => { setEditingOrder(null); setCreatingOrder(false); }}
        />
      )}
    </div>
  );
};

const InventoryView: React.FC<{ products: Product[], onEdit: (p: Product) => void, onDelete: (id: string) => void, onCreate: () => void }> = ({ products, onEdit, onDelete, onCreate }) => {
  const { t } = useLanguage();
  return (
  <div className="animate-fade-in space-y-6">
    <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
       <div className="text-xs text-gray-500 uppercase tracking-widest font-bold">{t('admin_total_items')}: {products.length}</div>
       <button type="button" onClick={onCreate} className="bg-white text-black hover:bg-gray-200 px-4 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider flex gap-2 items-center justify-center shrink-0"><Plus size={14} /> {t('admin_create_new')}</button>
    </div>
    <div className="bg-[#0A0A0A] border border-white/10 rounded-xl overflow-x-auto overscroll-x-contain">
      <table className="w-full min-w-[520px] text-left">
        <thead className="bg-[#111] border-b border-white/5 text-gray-500 text-[10px] uppercase tracking-widest">
           <tr><th className="p-4 pl-6 font-bold">{t('admin_th_item')}</th><th className="p-4 font-bold">{t('admin_th_value')}</th><th className="p-4 text-right pr-6 font-bold">{t('admin_th_controls')}</th></tr>
        </thead>
        <tbody className="divide-y divide-white/5">
           {products.map(p => {
             return (
             <tr key={p.id} className="hover:bg-white/[0.02] group transition-colors">
                <td className="p-4 pl-6 flex items-center gap-4">
                   <div className="w-10 h-10 rounded bg-[#151515] overflow-hidden border border-white/10 relative">
                      <AdminThumbnail src={p.coverImage} className="w-full h-full object-cover" />
                   </div>
                   <div>
                     <div className="font-bold text-sm text-white flex items-center gap-2">
                        {p.title}
                        {p.isNew && <span className="text-[8px] bg-cyan-500/20 text-cyan-400 border border-cyan-500/50 px-1.5 py-0.5 rounded uppercase font-bold">New</span>}
                        {p.isOnSale && <span className="text-[8px] bg-green-500/20 text-green-400 border border-green-500/50 px-1.5 py-0.5 rounded uppercase font-bold">%</span>}
                     </div>
                     <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-0.5">{p.categories.join(', ')}</div>
                   </div>
                </td>
                <td className="p-4 font-mono text-xs text-gray-300">
                   ${p.price}
                </td>
                <td className="p-4 text-right pr-6">
                   <button onClick={() => onEdit(p)} className="p-2 text-gray-500 hover:text-white transition-colors"><Edit3 size={14} /></button>
                   <button onClick={() => onDelete(p.id)} className="p-2 text-gray-500 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
                </td>
             </tr>
           )})}
        </tbody>
      </table>
    </div>
  </div>
  );
};

// --- PRODUCT EDITOR ---
const ProductDetailView: React.FC<{ product: Product, availableCategories: string[], onDeleteCategory: (cat: string) => void, onSave: (p: Product) => Promise<void>, onCancel: () => void }> = ({ product, availableCategories, onDeleteCategory, onSave, onCancel }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<Product>({ ...product });
  const [isSaving, setIsSaving] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [newGalleryUrl, setNewGalleryUrl] = useState('');
  const [newFeature, setNewFeature] = useState('');

  const handleAddCategory = () => {
    if (newCategory && !formData.categories.includes(newCategory)) {
      setFormData({ ...formData, categories: [...formData.categories, newCategory] });
      setNewCategory('');
    }
  };

  const toggleCategory = (cat: string) => {
    if (formData.categories.includes(cat)) {
       setFormData({...formData, categories: formData.categories.filter(c => c !== cat)});
    } else {
       setFormData({...formData, categories: [...formData.categories, cat]});
    }
  };

  const handleAddGalleryImage = () => {
    if (newGalleryUrl) {
      setFormData({...formData, gallery: [...formData.gallery, newGalleryUrl]});
      setNewGalleryUrl('');
    }
  };

  const handleAddFeature = () => {
    if (newFeature) {
        setFormData(prev => ({ ...prev, features: [...prev.features, newFeature] }));
        setNewFeature('');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    await onSave(formData);
    setIsSaving(false);
  };

  const effectiveAvailableCats = Array.from(new Set([...availableCategories, ...formData.categories]));
  const defaultCats = ['workflow', 'lipsync', 'dance', 'photo', 'nsfw'];

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 animate-fade-in">
       <div className="xl:col-span-2 space-y-6">
          <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6">
             <div className="grid grid-cols-1 gap-4">
                <InputGroup label={t('admin_grp_title')}>
                  <input type="text" value={formData.title} onChange={e => setFormData(prev => ({...prev, title: e.target.value}))} className="w-full bg-[#121212] border border-white/10 rounded-lg p-3 text-white focus:border-white/30 outline-none text-sm" placeholder="Enter title..." />
                </InputGroup>
                <div className="mb-4">
                    <label className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mb-2 flex items-center gap-2"><FileText size={12} /> {t('admin_grp_desc')}</label>
                    <MarkdownEditor value={formData.description} onChange={(val) => setFormData(prev => ({...prev, description: val}))} height="h-96" />
                </div>
             </div>
          </div>

          <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6">
             <InputGroup label={t('admin_grp_cover')} icon={ImageIcon}>
                <div className="flex gap-4 items-start">
                    <input type="text" value={formData.coverImage} onChange={e => setFormData(prev => ({...prev, coverImage: e.target.value}))} className="flex-1 bg-[#121212] border border-white/10 rounded-lg p-3 text-white text-xs font-mono" placeholder="https://..." />
                    <div className="w-16 h-16 bg-[#151515] rounded-lg overflow-hidden border border-white/10 shrink-0 relative">
                        <AdminThumbnail src={formData.coverImage} className="w-full h-full object-cover" />
                    </div>
                </div>
             </InputGroup>
             <InputGroup label={t('admin_grp_gallery')} icon={ImageIcon}>
                <div className="flex gap-2 mb-4">
                    <input type="text" value={newGalleryUrl} onChange={e => setNewGalleryUrl(e.target.value)} className="flex-1 bg-[#121212] border border-white/10 rounded-lg p-3 text-white text-xs font-mono" placeholder="Image or Video URL..." />
                    <button onClick={handleAddGalleryImage} className="bg-white/10 hover:bg-white/20 px-4 rounded-lg text-white font-bold"><Plus size={16}/></button>
                </div>
                <div className="grid grid-cols-5 gap-2">
                    {formData.gallery.map((url, i) => (
                      <div key={i} className="aspect-square relative group rounded border border-white/10 overflow-hidden bg-[#151515]">
                          <AdminThumbnail src={url} className="w-full h-full object-cover" />
                          <button onClick={() => setFormData(prev => ({...prev, gallery: prev.gallery.filter((_, idx) => idx !== i)}))} className="absolute top-1 right-1 bg-red-500/80 p-1 rounded text-white opacity-0 group-hover:opacity-100 transition-opacity"><X size={10} /></button>
                      </div>
                    ))}
                </div>
             </InputGroup>
             <InputGroup label={t('admin_grp_video')} icon={MonitorPlay}>
                <input type="text" value={formData.demoVideoUrl || ''} onChange={e => setFormData(prev => ({...prev, demoVideoUrl: e.target.value}))} className="w-full bg-[#121212] border border-white/10 rounded-lg p-3 text-white text-xs font-mono" placeholder="Preview Video URL..." />
             </InputGroup>
          </div>
       </div>

       <div className="space-y-6">
          <div className="bg-[#0A0A0A] border border-green-500/20 rounded-2xl p-6 relative overflow-hidden">
             <InputGroup label={t('admin_grp_delivery')} icon={Download}>
                <input type="text" value={formData.digitalAssetUrl || ''} onChange={e => setFormData(prev => ({...prev, digitalAssetUrl: e.target.value}))} className="w-full bg-[#121212] border border-green-500/30 rounded-lg p-3 text-white text-xs font-mono focus:border-green-500 outline-none" placeholder="Download Link..." />
             </InputGroup>
             <InputGroup label={t('admin_grp_access')}>
                <input type="text" value={formData.digitalAssetInstructions || ''} onChange={e => setFormData(prev => ({...prev, digitalAssetInstructions: e.target.value}))} className="w-full bg-[#121212] border border-green-500/30 rounded-lg p-3 text-white text-xs focus:border-green-500 outline-none" placeholder="Passwords / Notes..." />
             </InputGroup>
          </div>

          <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6">
             <div className="flex flex-col gap-3 mb-6">
               <label className="text-[10px] text-gray-500 font-bold uppercase tracking-widest flex items-center gap-2"><DollarSign size={12}/> {t('admin_grp_pricing')}</label>
               <div className="flex gap-2">
                   <button onClick={() => setFormData(prev => ({...prev, isOnSale: !prev.isOnSale}))} className={`flex-1 px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest border transition-all flex items-center justify-center gap-2 ${formData.isOnSale ? 'bg-green-500/10 border-green-500 text-green-500' : 'bg-[#151515] border-white/10 text-gray-500 hover:bg-white/5'}`}>
                      <Percent size={12} /> {formData.isOnSale ? 'On Sale' : 'Standard'}
                   </button>
                   <button onClick={() => setFormData(prev => ({...prev, isNew: !prev.isNew}))} className={`flex-1 px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest border transition-all flex items-center justify-center gap-2 ${formData.isNew ? 'bg-cyan-500/10 border-cyan-500 text-cyan-400' : 'bg-[#151515] border-white/10 text-gray-500 hover:bg-white/5'}`}>
                      <Sparkles size={12} /> {formData.isNew ? 'New Arrival' : 'Classic'}
                   </button>
               </div>
             </div>
             
             {formData.isOnSale && (
                <div className="mb-6 p-4 bg-green-900/10 border border-green-500/20 rounded-xl animate-fade-in">
                    <div className="flex justify-between items-center mb-3">
                        <label className="text-[10px] text-green-400 font-bold uppercase tracking-widest flex items-center gap-2">
                            <Clock size={12} /> Sale Timer Config
                        </label>
                        <button onClick={() => setFormData({...formData, saleEndsAt: undefined})} className="text-[9px] text-red-400 hover:text-red-300 uppercase font-bold">Clear Timer</button>
                    </div>

                    {/* PRESETS DROPDOWN */}
                    <div className="mb-4">
                        <select 
                            className="w-full bg-[#050505] border border-green-500/30 rounded-lg p-2 text-xs text-green-300 font-mono focus:border-green-500 outline-none cursor-pointer hover:bg-green-900/20 transition-colors"
                            onChange={(e) => {
                                if(e.target.value) {
                                    const mins = parseInt(e.target.value);
                                    const date = new Date();
                                    date.setTime(date.getTime() + mins * 60 * 1000);
                                    setFormData(prev => ({...prev, saleEndsAt: date.toISOString()}));
                                    e.target.value = ""; // Reset dropdown
                                }
                            }}
                            defaultValue=""
                        >
                            <option value="" disabled>⚡️ Quick Add Duration...</option>
                            <option value="10">10 Minutes</option>
                            <option value="15">15 Minutes</option>
                            <option value="30">30 Minutes</option>
                            <option value="60">1 Hour</option>
                            <option value="180">3 Hours</option>
                            <option value="300">5 Hours</option>
                            <option value="480">8 Hours</option>
                            <option value="600">10 Hours</option>
                            <option value="720">12 Hours</option>
                            <option value="1080">18 Hours</option>
                            <option value="1440">24 Hours</option>
                            <option value="2880">2 Days</option>
                            <option value="4320">3 Days</option>
                            <option value="10080">7 Days</option>
                        </select>
                    </div>

                    {/* MANUAL INPUTS */}
                    <div className="grid grid-cols-4 gap-2">
                        {/* Helper to parse current date safely */}
                        {(() => {
                            const dateVal = formData.saleEndsAt ? new Date(formData.saleEndsAt) : new Date();
                            const safeDate = isNaN(dateVal.getTime()) ? new Date() : dateVal;
                            
                            const updateDate = (field: 'y'|'m'|'d'|'t', val: string) => {
                                const newDate = new Date(safeDate);
                                if (field === 'd') newDate.setDate(parseInt(val) || 1);
                                if (field === 'm') newDate.setMonth(parseInt(val));
                                if (field === 'y') newDate.setFullYear(parseInt(val) || newDate.getFullYear());
                                if (field === 't') {
                                    const [h, m] = val.split(':');
                                    newDate.setHours(parseInt(h)||0);
                                    newDate.setMinutes(parseInt(m)||0);
                                }
                                setFormData(prev => ({...prev, saleEndsAt: newDate.toISOString()}));
                            };

                            return (
                                <>
                                    <div className="col-span-1">
                                        <label className="text-[8px] text-green-500/50 uppercase font-bold block mb-1">Day</label>
                                        <input 
                                            type="number" 
                                            min="1" max="31"
                                            value={safeDate.getDate()}
                                            onChange={(e) => updateDate('d', e.target.value)}
                                            className="w-full bg-[#050505] border border-green-500/30 rounded p-2 text-xs text-white text-center font-mono focus:border-green-500 outline-none"
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <label className="text-[8px] text-green-500/50 uppercase font-bold block mb-1">Month</label>
                                        <select 
                                            value={safeDate.getMonth()}
                                            onChange={(e) => updateDate('m', e.target.value)}
                                            className="w-full bg-[#050505] border border-green-500/30 rounded p-2 text-xs text-white font-mono focus:border-green-500 outline-none appearance-none text-center"
                                        >
                                            {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                                                <option key={i} value={i}>{m}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-span-1">
                                        <label className="text-[8px] text-green-500/50 uppercase font-bold block mb-1">Year</label>
                                        <input 
                                            type="number" 
                                            value={safeDate.getFullYear()}
                                            onChange={(e) => updateDate('y', e.target.value)}
                                            className="w-full bg-[#050505] border border-green-500/30 rounded p-2 text-xs text-white text-center font-mono focus:border-green-500 outline-none"
                                        />
                                    </div>
                                    <div className="col-span-1">
                                        <label className="text-[8px] text-green-500/50 uppercase font-bold block mb-1">Time</label>
                                        <input 
                                            type="time" 
                                            value={`${String(safeDate.getHours()).padStart(2, '0')}:${String(safeDate.getMinutes()).padStart(2, '0')}`}
                                            onChange={(e) => updateDate('t', e.target.value)}
                                            className="w-full bg-[#050505] border border-green-500/30 rounded p-2 text-xs text-white text-center font-mono focus:border-green-500 outline-none"
                                        />
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                    
                    {/* Helper Text */}
                    <div className="mt-2 text-right">
                         <span className="text-[9px] text-green-500/70 font-mono">
                            Expires: {formData.saleEndsAt ? new Date(formData.saleEndsAt).toLocaleString() : 'Never'}
                         </span>
                    </div>
                </div>
             )}

             <div className="space-y-4">
                 <div>
                    <label className="text-[9px] text-gray-500 uppercase tracking-widest block mb-1">{t('admin_lbl_current')}</label>
                    <input type="number" value={formData.price} onChange={e => setFormData(prev => ({...prev, price: parseFloat(e.target.value)}))} className="w-full bg-[#121212] border border-white/10 rounded-lg p-3 text-white font-bold text-lg" />
                 </div>
                 {formData.isOnSale && (
                   <div className="animate-fade-in">
                      <label className="text-[9px] text-red-400 uppercase tracking-widest block mb-1">{t('admin_lbl_old')}</label>
                      <input type="number" value={formData.originalPrice || ''} onChange={e => setFormData(prev => ({...prev, originalPrice: parseFloat(e.target.value)}))} className="w-full bg-[#121212] border border-red-500/30 rounded-lg p-3 text-white font-bold text-lg" placeholder="0.00" />
                   </div>
                 )}
             </div>
          </div>
          
          <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6">
             <InputGroup label={t('admin_lbl_tech')} icon={Cpu}>
                <div className="space-y-2 mb-3">
                    {formData.features.map((feat, i) => (
                        <div key={i} className="flex justify-between items-center bg-[#151515] border border-white/5 rounded px-3 py-2">
                            <span className="text-xs text-gray-300 font-mono">{feat}</span>
                            <button onClick={() => setFormData(prev => ({...prev, features: prev.features.filter((_, idx) => idx !== i)}))} className="text-red-500 hover:text-white"><X size={12}/></button>
                        </div>
                    ))}
                </div>
                <div className="flex gap-2">
                    <input type="text" value={newFeature} onChange={e => setNewFeature(e.target.value)} placeholder="e.g. 4K Resolution..." className="flex-1 bg-[#121212] border border-white/10 rounded-lg p-2 text-xs text-white" onKeyDown={(e) => e.key === 'Enter' && handleAddFeature()}/>
                    <button onClick={handleAddFeature} className="bg-white/10 hover:bg-white/20 p-2 rounded-lg text-white"><Plus size={14}/></button>
                </div>
             </InputGroup>
          </div>

          <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6">
             <InputGroup label={t('admin_lbl_tags')} icon={Tag}>
                 <div className="flex flex-wrap gap-2 mb-4 max-h-40 overflow-y-auto custom-scrollbar p-1">
                    {effectiveAvailableCats.map(cat => {
                        const isActive = formData.categories.includes(cat);
                        const isDefault = defaultCats.includes(cat);
                        return (
                           <div key={cat} className={`group/tag flex items-center rounded-full text-[10px] uppercase font-bold border transition-all ${isActive ? 'bg-white text-black border-white' : 'bg-transparent border-white/10 text-gray-500 hover:border-white/30'}`}>
                               <button onClick={() => toggleCategory(cat)} className="px-3 py-1.5 flex items-center gap-2">
                                  {cat}
                               </button>
                               {/* ⚡️ DELETE CATEGORY BUTTON (Always Visible for Custom Tags) */}
                               {!isDefault && (
                                   <button 
                                     type="button"
                                     onClick={(e) => { 
                                         e.preventDefault();
                                         e.stopPropagation(); 
                                         onDeleteCategory(cat); 
                                     }}
                                     className="pr-2 pl-1 py-1.5 text-red-400 hover:text-red-200 border-l border-white/10 hover:bg-red-500/20 rounded-r-full transition-colors flex items-center"
                                     title="Delete category globally from database"
                                   >
                                      <Trash2 size={10} />
                                   </button>
                               )}
                           </div>
                        )
                    })}
                 </div>
                 <div className="flex gap-2">
                    <input type="text" value={newCategory} onChange={e => setNewCategory(e.target.value)} placeholder="Create Tag..." className="flex-1 bg-[#121212] border border-white/10 rounded-lg p-2 text-xs text-white" />
                    <button onClick={handleAddCategory} className="bg-white/10 hover:bg-white/20 p-2 rounded-lg text-white"><Plus size={14}/></button>
                 </div>
             </InputGroup>
          </div>

          <div className="flex gap-3 pt-4">
             <button onClick={onCancel} className="flex-1 py-3 rounded-lg border border-white/10 text-gray-400 hover:text-white font-bold text-xs uppercase tracking-widest">{t('admin_btn_cancel')}</button>
             <button onClick={handleSave} disabled={isSaving} className="flex-1 bg-white text-black hover:bg-gray-200 disabled:opacity-50 rounded-lg font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2">
                {isSaving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {t('admin_btn_save')}
             </button>
          </div>
       </div>
    </div>
  );
};

// --- UPDATED CMS VIEW WITH FLUID DRAG AND DROP ---
const CMSView: React.FC<{ 
  products: Product[], 
  siteConfig: SiteConfig, 
  onUpdate: () => void, 
  onPreview: (url: string, type: 'video' | 'image') => void 
}> = ({ products, siteConfig, onUpdate, onPreview }) => {
  const { t } = useLanguage();
  const [localConfig, setLocalConfig] = useState<SiteConfig>(siteConfig);
  const [isSaving, setIsSaving] = useState(false);
  
  // Drag State
  const dragHeroItem = useRef<number | null>(null);
  const dragOverHeroItem = useRef<number | null>(null);
  
  const dragFeaturedItem = useRef<number | null>(null);
  const dragOverFeaturedItem = useRef<number | null>(null);

  const HERO_COLORS = ['bg-purple-500', 'bg-blue-500', 'bg-green-500', 'bg-red-500', 'bg-orange-500', 'bg-pink-500', 'bg-yellow-500', 'bg-indigo-500'];

  useEffect(() => { setLocalConfig(siteConfig) }, [siteConfig]);

  const handleSaveConfig = async () => {
    setIsSaving(true);
    const cleanVideos = localConfig.heroVideos.map(vid => ({
        ...vid,
        url: vid.type === 'video' ? normalizeVideoUrl(vid.url) : vid.url
    }));
    const v = await DB.updateSiteConfig('hero_videos', cleanVideos);
    const f = await DB.updateSiteConfig('featured_ids', localConfig.featuredProductIds);
    // Не перезаписывать landing_content null, если пользователь не трогал блок — сохраняем только при наличии данных
    const l = localConfig.landingContent
      ? await DB.updateSiteConfig('landing_content', localConfig.landingContent)
      : true;
    if (v && f && l) onUpdate();
    else alert("Failed to save configuration");
    setIsSaving(false);
  };

  const defaultLanding = (): LandingContent => ({
    pain: {
      title: t('pain_title'),
      problemTitle: t('pain_box_title_1'),
      solutionTitle: t('pain_box_title_2'),
      problems: [t('pain_p_1'), t('pain_p_2'), t('pain_p_3'), t('pain_p_4')],
      solutions: [t('pain_s_1'), t('pain_s_2'), t('pain_s_3'), t('pain_s_4'), t('pain_s_5')],
    },
    stats: [
      { number: '500+', label: t('proof_stat_1') },
      { number: '+280%', label: t('proof_stat_2'), highlight: true },
      { number: '4.9/5', label: t('proof_stat_3') },
    ],
    cta: {
      title: t('cta_title'),
      text: t('cta_text'),
      btnLabel: t('cta_btn_tg'),
      btnUrl: 'https://t.me/miranovui',
    },
    heroBlock: {
      titleTop: t('hero_title_top'),
      titleBottom: t('hero_title_bottom'),
      subtitle: t('hero_title_2'),
      shopBtnLabel: t('hero_btn_shop'),
      contactBtnLabel: t('hero_btn_contact'),
      contactBtnUrl: 'https://t.me/miranovui',
    },
  });

  const landing = localConfig.landingContent ?? defaultLanding();

  // --- FLUID DRAG & DROP LOGIC (Live Sorting) ---
  
  const handleHeroSort = () => {
    const dragIdx = dragHeroItem.current;
    const overIdx = dragOverHeroItem.current;
    if (dragIdx === null || overIdx === null || dragIdx === overIdx) return;

    // Create shallow copy
    const items = [...localConfig.heroVideos];
    // Splice logic: remove from old, insert at new
    const draggedItemContent = items[dragIdx];
    items.splice(dragIdx, 1);
    items.splice(overIdx, 0, draggedItemContent);

    // Update state immediately to show visual shift
    setLocalConfig(prev => ({ ...prev, heroVideos: items }));
    
    // Update ref so subsequent drags track correctly
    dragHeroItem.current = overIdx;
  };

  const handleFeaturedSort = () => {
    const dragIdx = dragFeaturedItem.current;
    const overIdx = dragOverFeaturedItem.current;
    if (dragIdx === null || overIdx === null || dragIdx === overIdx) return;

    const items = [...localConfig.featuredProductIds];
    const draggedItemContent = items[dragIdx];
    items.splice(dragIdx, 1);
    items.splice(overIdx, 0, draggedItemContent);

    setLocalConfig(prev => ({ ...prev, featuredProductIds: items }));
    dragFeaturedItem.current = overIdx;
  };

  // --- CRUD Operations ---
  const addVideo = () => {
    const newVid: HeroItem = { title: 'New Item', color: 'bg-purple-500', delay: 0, url: '', type: 'video', isNSFW: false };
    setLocalConfig(prev => ({ ...prev, heroVideos: [...prev.heroVideos, newVid] }));
  };

  const removeVideo = (idx: number) => {
    setLocalConfig(prev => ({ ...prev, heroVideos: prev.heroVideos.filter((_, i) => i !== idx) }));
  };

  const updateVideo = (idx: number, field: keyof HeroItem, val: any) => {
    const newVids = [...localConfig.heroVideos];
    newVids[idx] = { ...newVids[idx], [field]: val };
    setLocalConfig(prev => ({ ...prev, heroVideos: newVids }));
  };

  const toggleFeaturedProduct = (id: string) => {
    let ids = [...localConfig.featuredProductIds];
    if (ids.includes(id)) ids = ids.filter(i => i !== id);
    else ids.push(id);
    setLocalConfig(prev => ({ ...prev, featuredProductIds: ids }));
  };

  const selectedProducts = localConfig.featuredProductIds
    .map(id => products.find(p => p.id === id))
    .filter((p): p is Product => !!p);

  const availableProducts = products.filter(p => !localConfig.featuredProductIds.includes(p.id));

  const updateLanding = (path: 'pain' | 'stats' | 'cta' | 'heroBlock', field: string, value: any) => {
    const def = defaultLanding();
    const next: LandingContent = {
      heroBlock: { ...def.heroBlock!, ...landing.heroBlock },
      pain: { ...def.pain!, ...landing.pain },
      stats: [...(landing.stats ?? def.stats!)],
      cta: { ...def.cta!, ...landing.cta },
    };
    if (path === 'pain') {
      if (field === 'problems' || field === 'solutions') next.pain![field] = value;
      else (next.pain as any)![field] = value;
    } else if (path === 'stats') {
      const [idx, sub] = field.split('.');
      const i = parseInt(idx, 10);
      if (!isNaN(i) && next.stats![i]) {
        next.stats![i] = sub ? { ...next.stats![i], [sub]: value } : value;
      }
    } else if (path === 'cta') {
      (next.cta as any)![field] = value;
    } else if (path === 'heroBlock') {
      if (!next.heroBlock) next.heroBlock = defaultLanding().heroBlock!;
      (next.heroBlock as any)![field] = value;
    }
    setLocalConfig(prev => ({ ...prev, landingContent: next }));
  };

  const ensureLanding = () => {
    if (!localConfig.landingContent) {
      setLocalConfig(prev => ({ ...prev, landingContent: defaultLanding() }));
    }
  };

  const hero = localConfig.landingContent?.heroBlock ?? defaultLanding().heroBlock!;

  return (
    <div className="animate-fade-in space-y-8 pb-20">
      
      {/* ⚡️ HERO BLOCK (миниатюра с live preview) */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6">
        <h3 className="text-sm font-bold uppercase tracking-widest text-white mb-6 flex items-center gap-2">
          <Sparkles size={16} className="text-indigo-400" /> {t('admin_hero_block_title')}
        </h3>
        <p className="text-[10px] text-gray-500 mb-6 uppercase tracking-wider">{t('admin_hero_block_desc')}</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-3">
            <label className="text-[9px] text-gray-500 uppercase block">{t('admin_hero_title_top')}</label>
            <input value={hero?.titleTop ?? ''} onChange={e => { ensureLanding(); updateLanding('heroBlock', 'titleTop', e.target.value); }} className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-sm text-white" placeholder={t('hero_title_top')} />
            <label className="text-[9px] text-gray-500 uppercase block">{t('admin_hero_title_bottom')}</label>
            <input value={hero?.titleBottom ?? ''} onChange={e => { ensureLanding(); updateLanding('heroBlock', 'titleBottom', e.target.value); }} className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-sm text-white" placeholder={t('hero_title_bottom')} />
            <label className="text-[9px] text-gray-500 uppercase block">{t('admin_hero_subtitle')}</label>
            <input value={hero?.subtitle ?? ''} onChange={e => { ensureLanding(); updateLanding('heroBlock', 'subtitle', e.target.value); }} className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-sm text-white" placeholder={t('hero_title_2')} />
            <label className="text-[9px] text-gray-500 uppercase block">{t('admin_hero_shop_btn')}</label>
            <input value={hero?.shopBtnLabel ?? ''} onChange={e => { ensureLanding(); updateLanding('heroBlock', 'shopBtnLabel', e.target.value); }} className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-sm text-white" placeholder={t('hero_btn_shop')} />
            <label className="text-[9px] text-gray-500 uppercase block">{t('admin_hero_contact_btn')}</label>
            <input value={hero?.contactBtnLabel ?? ''} onChange={e => { ensureLanding(); updateLanding('heroBlock', 'contactBtnLabel', e.target.value); }} className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-sm text-white" placeholder={t('hero_btn_contact')} />
            <label className="text-[9px] text-gray-500 uppercase block">{t('admin_hero_contact_url')}</label>
            <input value={hero?.contactBtnUrl ?? ''} onChange={e => { ensureLanding(); updateLanding('heroBlock', 'contactBtnUrl', e.target.value); }} className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-sm text-white font-mono" placeholder="https://t.me/..." />
          </div>
          <div className="bg-[#080808] border border-white/10 rounded-xl p-6 flex flex-col items-center justify-center min-h-[280px]">
            <div className="text-center w-full">
              <h1 className="font-display font-black text-2xl md:text-3xl leading-tight tracking-tighter text-white uppercase mb-2">
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-red-900 via-red-500 to-red-900">{(hero?.titleTop || t('hero_title_top')).trim() || t('hero_title_top')}</span>
                <span className="block text-white/90">{(hero?.titleBottom || t('hero_title_bottom')).trim() || t('hero_title_bottom')}</span>
              </h1>
              <p className="text-[10px] text-white/50 mb-4 max-w-xs mx-auto">{(hero?.subtitle || t('hero_title_2')).trim() || t('hero_title_2')}</p>
              <div className="flex gap-2 justify-center flex-wrap">
                <span className="px-4 py-2 rounded-full bg-white/10 text-white text-[10px] font-bold uppercase">{(hero?.shopBtnLabel || t('hero_btn_shop')).trim() || t('hero_btn_shop')}</span>
                <span className="px-4 py-2 rounded-full border border-white/20 text-white/80 text-[10px] font-bold uppercase">{(hero?.contactBtnLabel || t('hero_btn_contact')).trim() || t('hero_btn_contact')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ⚡️ LANDING CONTENT — Pain, Stats, CTA */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6">
        <h3 className="text-sm font-bold uppercase tracking-widest text-white mb-6 flex items-center gap-2">
          <FileText size={16} /> {t('admin_landing_title')}
        </h3>
        <p className="text-[10px] text-gray-500 mb-6 uppercase tracking-wider">{t('admin_landing_desc')}</p>

        {/* Block 1: Pain */}
        <div className="mb-8 p-4 bg-[#080808] rounded-xl border border-white/5">
          <h4 className="text-xs font-bold text-red-400 uppercase tracking-widest mb-4">1. {t('admin_landing_block1')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label className="text-[9px] text-gray-500 uppercase block">{t('admin_landing_block1_title')}</label>
              <input value={landing.pain?.title ?? ''} onChange={e => { ensureLanding(); updateLanding('pain', 'title', e.target.value); }} className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-sm text-white" placeholder={t('pain_title')} />
              <label className="text-[9px] text-gray-500 uppercase block">{t('admin_landing_block1_problem')}</label>
              <input value={landing.pain?.problemTitle ?? ''} onChange={e => { ensureLanding(); updateLanding('pain', 'problemTitle', e.target.value); }} className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-sm text-white" />
              <label className="text-[9px] text-gray-500 uppercase block">{t('admin_landing_block1_problems')}</label>
              <textarea value={(landing.pain?.problems ?? []).join('\n')} onChange={e => { ensureLanding(); updateLanding('pain', 'problems', e.target.value.split('\n').filter(Boolean)); }} rows={4} className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-sm text-white font-mono" />
            </div>
            <div className="space-y-3">
              <label className="text-[9px] text-gray-500 uppercase block">{t('admin_landing_block1_solution')}</label>
              <input value={landing.pain?.solutionTitle ?? ''} onChange={e => { ensureLanding(); updateLanding('pain', 'solutionTitle', e.target.value); }} className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-sm text-white" />
              <label className="text-[9px] text-gray-500 uppercase block">{t('admin_landing_block1_solutions')}</label>
              <textarea value={(landing.pain?.solutions ?? []).join('\n')} onChange={e => { ensureLanding(); updateLanding('pain', 'solutions', e.target.value.split('\n').filter(Boolean)); }} rows={5} className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-sm text-white font-mono" />
            </div>
          </div>
        </div>

        {/* Block 2: Stats */}
        <div className="mb-8 p-4 bg-[#080808] rounded-xl border border-white/5">
          <h4 className="text-xs font-bold text-green-400 uppercase tracking-widest mb-4">2. {t('admin_landing_block2')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(landing.stats && landing.stats.length > 0 ? landing.stats : defaultLanding().stats!).map((s, i) => (
              <div key={i} className="flex gap-3 items-center p-3 bg-[#121212] rounded-lg">
                <input value={s.number} onChange={e => updateLanding('stats', `${i}.number`, e.target.value)} className="w-20 bg-[#050505] border border-white/10 rounded p-2 text-sm font-bold text-white" placeholder="500+" />
                <input value={s.label} onChange={e => updateLanding('stats', `${i}.label`, e.target.value)} className="flex-1 bg-[#050505] border border-white/10 rounded p-2 text-xs text-white" placeholder={t('proof_stat_1')} />
                <label className="flex items-center gap-2 text-[9px] text-gray-500 shrink-0">
                  <input type="checkbox" checked={!!s.highlight} onChange={e => updateLanding('stats', `${i}.highlight`, e.target.checked)} />
                  {t('admin_landing_block2_highlight')}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Block 3: CTA */}
        <div className="p-4 bg-[#080808] rounded-xl border border-white/5">
          <h4 className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-4">3. {t('admin_landing_block3')}</h4>
          <div className="space-y-3">
            <label className="text-[9px] text-gray-500 uppercase block">{t('admin_landing_block3_title')}</label>
            <input value={landing.cta?.title ?? ''} onChange={e => { ensureLanding(); updateLanding('cta', 'title', e.target.value); }} className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-sm text-white" placeholder={t('cta_title')} />
            <label className="text-[9px] text-gray-500 uppercase block">{t('admin_landing_block3_text')}</label>
            <textarea value={landing.cta?.text ?? ''} onChange={e => { ensureLanding(); updateLanding('cta', 'text', e.target.value); }} rows={3} className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-sm text-white" placeholder={t('cta_text')} />
            <label className="text-[9px] text-gray-500 uppercase block">{t('admin_landing_block3_btn')}</label>
            <input value={landing.cta?.btnLabel ?? ''} onChange={e => { ensureLanding(); updateLanding('cta', 'btnLabel', e.target.value); }} className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-sm text-white" placeholder={t('cta_btn_tg')} />
            <label className="text-[9px] text-gray-500 uppercase block">{t('admin_landing_block3_url')}</label>
            <input value={landing.cta?.btnUrl ?? ''} onChange={e => { ensureLanding(); updateLanding('cta', 'btnUrl', e.target.value); }} className="w-full bg-[#121212] border border-white/10 rounded-lg p-2 text-sm text-white font-mono" placeholder="https://t.me/..." />
          </div>
        </div>
      </div>

      {/* ⚡️ HERO CONFIG (FLUID DRAG) */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-sm font-bold uppercase tracking-widest text-white">{t('admin_cms_hero')}</h3>
          <button onClick={addVideo} className="bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider text-white flex items-center gap-2">
            <Plus size={12} /> {t('admin_cms_add_slide')}
          </button>
        </div>
        
        <div className="space-y-4">
          <p className="text-[9px] text-gray-500 uppercase tracking-widest mb-2 flex items-center gap-2"><Move size={10}/> {t('admin_cms_drag_hint')}</p>
          {localConfig.heroVideos.map((vid, idx) => (
             <div 
                key={idx} // Using index as key is acceptable here for reordering state
                draggable
                onDragStart={() => (dragHeroItem.current = idx)}
                onDragEnter={() => { 
                    dragOverHeroItem.current = idx;
                    handleHeroSort(); // 🔥 Live Swap
                }}
                onDragEnd={() => {
                    dragHeroItem.current = null;
                    dragOverHeroItem.current = null;
                }}
                onDragOver={(e) => e.preventDefault()}
                className={`
                    bg-[#121212] border border-white/5 p-4 rounded-xl flex flex-col md:flex-row gap-4 items-start relative overflow-hidden group hover:border-white/20 transition-all duration-300
                    cursor-move
                `}
             >
                <div className="pl-0 flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                   <div className="md:col-span-2 flex gap-4 items-start">
                      
                      {/* Thumbnail with NSFW Blur */}
                      <div className="w-20 h-32 rounded-lg overflow-hidden shrink-0 border border-white/10 flex items-center justify-center bg-black cursor-pointer relative shadow-lg group-hover:scale-105 transition-transform" onClick={() => vid.url && onPreview(normalizeVideoUrl(vid.url), vid.type)}>
                         <div className={`w-full h-full ${vid.isNSFW ? 'blur-sm' : ''}`}>
                            <AdminThumbnail src={vid.url || ''} className="w-full h-full object-cover" />
                         </div>
                         {vid.isNSFW && <div className="absolute inset-0 flex items-center justify-center"><EyeOff className="text-red-500 drop-shadow-md" size={24} /></div>}
                      </div>

                      <div className="flex-1 flex flex-col gap-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="text-[9px] block mb-1 uppercase font-bold text-gray-500">{t('admin_cms_title_lbl')}</label><input type="text" value={vid.title} onChange={e => updateVideo(idx, 'title', e.target.value)} className="w-full bg-[#050505] border border-white/10 rounded p-2 text-xs text-white" /></div>
                            <div><label className="text-[9px] block mb-1 uppercase font-bold text-gray-500">{t('admin_cms_url_lbl')}</label><input type="text" value={vid.url} onChange={e => updateVideo(idx, 'url', e.target.value)} className="w-full bg-[#050505] border border-white/10 rounded p-2 text-xs text-white" /></div>
                        </div>
                        
                        {/* SETTINGS ROW: TYPE | COLOR | NSFW | DELAY */}
                        <div className="flex flex-wrap items-center gap-4 bg-[#080808] p-2 rounded-lg border border-white/5">
                            
                            {/* Type Toggle */}
                            <div className="flex gap-1">
                                <button onClick={() => updateVideo(idx, 'type', 'video')} className={`p-1.5 rounded ${vid.type === 'video' ? 'bg-white text-black' : 'text-gray-500 hover:text-white'}`} title={t('admin_cms_type_vid')}><MonitorPlay size={14}/></button>
                                <button onClick={() => updateVideo(idx, 'type', 'image')} className={`p-1.5 rounded ${vid.type === 'image' ? 'bg-white text-black' : 'text-gray-500 hover:text-white'}`} title={t('admin_cms_type_img')}><ImageIcon size={14}/></button>
                            </div>

                            <div className="w-px h-4 bg-white/10" />

                            {/* Color Picker */}
                            <div className="flex gap-1">
                                {HERO_COLORS.map(color => (
                                    <button 
                                        key={color} 
                                        onClick={() => updateVideo(idx, 'color', color)}
                                        className={`w-4 h-4 rounded-full border transition-all ${vid.color === color ? 'border-white scale-110' : 'border-transparent opacity-50 hover:opacity-100'} ${color}`}
                                        title={color}
                                    />
                                ))}
                            </div>

                            <div className="w-px h-4 bg-white/10" />

                            {/* NSFW Toggle */}
                            <button 
                                onClick={() => updateVideo(idx, 'isNSFW', !vid.isNSFW)}
                                className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded transition-colors ${vid.isNSFW ? 'bg-red-900/30 text-red-400 border border-red-500/30' : 'text-gray-500 hover:text-white'}`}
                            >
                                <EyeOff size={12} /> {vid.isNSFW ? t('admin_cms_nsfw_on') : t('admin_cms_nsfw_off')}
                            </button>

                            <div className="w-px h-4 bg-white/10" />

                            {/* Delay */}
                            <div className="flex items-center gap-2">
                                <span className="text-[9px] text-gray-500 uppercase font-bold">{t('admin_cms_delay_lbl')}</span>
                                <input type="number" step="0.1" value={vid.delay} onChange={e => updateVideo(idx, 'delay', parseFloat(e.target.value))} className="w-12 bg-[#151515] text-white text-[10px] p-1 rounded border border-white/10 text-center" />
                            </div>

                        </div>
                      </div>
                   </div>
                </div>
                <button onClick={() => removeVideo(idx)} className="self-center p-2 bg-red-500/10 text-red-500 rounded hover:bg-red-500/20 transition-colors ml-2"><Trash2 size={14} /></button>
                <div className="absolute right-2 top-2 text-gray-700 opacity-50 group-hover:opacity-100"><GripVertical size={16}/></div>
             </div>
          ))}
        </div>
      </div>

      {/* DRAG & DROP SHOWCASE EDITOR (FLUID) */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6">
        <h3 className="text-sm font-bold uppercase tracking-widest text-white mb-6 flex items-center gap-2"><Sparkles size={16} className="text-yellow-500" /> {t('admin_cms_showcase')}</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* COLUMN 1: ACTIVE (SORTABLE) */}
            <div>
                <h4 className="text-[10px] font-bold uppercase tracking-widest text-green-400 mb-4 pb-2 border-b border-white/10">{t('admin_cms_active_list')} ({selectedProducts.length})</h4>
                <div className="space-y-2 min-h-[100px]">
                    {selectedProducts.map((p, idx) => (
                        <div 
                            key={p.id}
                            draggable
                            onDragStart={() => (dragFeaturedItem.current = idx)}
                            onDragEnter={() => {
                                dragOverFeaturedItem.current = idx;
                                handleFeaturedSort();
                            }}
                            onDragEnd={() => {
                                dragFeaturedItem.current = null;
                                dragOverFeaturedItem.current = null;
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            className={`
                                flex items-center gap-3 p-3 bg-[#151515] border border-white/10 rounded-xl cursor-move hover:border-white/30 transition-all duration-300
                            `}
                        >
                            <div className="text-gray-600"><GripVertical size={14} /></div>
                            <div className="w-8 h-8 rounded overflow-hidden bg-black shrink-0">
                                <AdminThumbnail src={p.coverImage} className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-bold text-white truncate">{p.title}</div>
                                <div className="text-[9px] text-gray-500">${p.price}</div>
                            </div>
                            <button onClick={() => toggleFeaturedProduct(p.id)} className="text-red-500 hover:bg-red-500/10 p-2 rounded"><X size={14}/></button>
                        </div>
                    ))}
                    {selectedProducts.length === 0 && <div className="text-center py-8 text-gray-600 text-xs italic">Drag items here or click + from list</div>}
                </div>
            </div>

            {/* COLUMN 2: AVAILABLE */}
            <div>
                <h4 className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-4 pb-2 border-b border-white/10">{t('admin_cms_avail_list')}</h4>
                <div className="grid grid-cols-1 gap-2 max-h-[400px] overflow-y-auto custom-scrollbar">
                    {availableProducts.map(p => (
                        <div key={p.id} className="flex items-center gap-3 p-3 bg-[#121212] border border-white/5 rounded-xl hover:bg-white/5 transition-colors cursor-pointer group" onClick={() => toggleFeaturedProduct(p.id)}>
                            <div className="w-8 h-8 rounded overflow-hidden bg-black shrink-0 opacity-50 group-hover:opacity-100">
                                <AdminThumbnail src={p.coverImage} className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-bold text-gray-400 group-hover:text-white truncate">{p.title}</div>
                            </div>
                            <div className="text-green-500 opacity-0 group-hover:opacity-100"><PlusCircle size={16} /></div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
      </div>

      {/* Кнопка сохранения внизу после всех блоков */}
      <div className="pt-8 pb-4 flex justify-center">
        <button onClick={handleSaveConfig} disabled={isSaving} className="bg-white text-black hover:bg-gray-200 px-10 py-4 rounded-full font-bold uppercase tracking-widest shadow-2xl flex items-center gap-2 transition-transform hover:scale-105">
          {isSaving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} {t('admin_cms_save')}
        </button>
      </div>
    </div>
  );
};